import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import classNames from "classnames";
import { localize } from "client_legacy/ndaw/culture";
import { ButtonBase } from "client/Components";
import { useHubImageViewerDialog } from "../HubImageViewerDialog";
import styles from "./PostImages.module.less";
import {
	ensureNotNil,
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import { CommunityPostImageViewModel } from "client/Api/Communities/communityPostViewer";
import { getCommunityPostImageUrl } from "./Composer/common";

export type PostImagesProps = {
	images: readonly CommunityPostImageViewModel[];
	trackingId: string;
};

export const PostImages = (props: PostImagesProps): ReactElement | null => {
	const {
		images: imagesFromProp,
		trackingId
	} = props;

	const [imageViewerDialog, openImageViewerDialog] = useHubImageViewerDialog();

	const images = useMemo(
		(): readonly CommunityPostImageWithUrl[] => imagesFromProp.map(
				(x): CommunityPostImageWithUrl => ({
				...x,
				url: getCommunityPostImageUrl(x.blobId)
			})
		),
		[imagesFromProp]
	);

	const openImageHandler = useCallback(
		(index: number): void => {
			const initialImageUrl = ensureNotNil(images[index])
				.url;

			openImageViewerDialog({
				initialImageUrl,
				images: images.map((x) => ({
					url: x.url
				})),
			});
		},
		[openImageViewerDialog, images]
	);

	if (images.length === 0) {
		return null;
	}

	const visibleImages: readonly CommunityPostImageWithUrl[] = images.slice(0, 3);

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
			>
				<ul
					className={classNames(
						styles.grid,
						gridLayoutStyleMap[visibleImages.length]
					)}
				>
					{visibleImages.map((x, index): ReactElement => (
						<li key={x.id}>
							<ImagePreview
								image={x}
								onClick={() => openImageHandler(index)}
								trackingId={generateTrackingId("image", `|${x.id}`)}
								overlayText={
									images.length > 3 && index === 2
										? `+${images.length - 3}`
										: undefined
								}
							/>
						</li>
					))}
				</ul>
			</div>
			{imageViewerDialog}
		</>
	);
};
PostImages.displayName = "PostImages";

const gridLayoutStyleMap: Record<number, string> = {
	[1]: styles.grid_one,
	[2]: styles.grid_two,
	[3]: styles.grid_three
};

type CommunityPostImageWithUrl = CommunityPostImageViewModel & {
	url: string;
};

type ImagePreviewProps = {
	image: CommunityPostImageWithUrl;
	onClick: () => void;
	overlayText?: string;
	trackingId: string;
};

const ImagePreview = (props: ImagePreviewProps): ReactElement => {
	const {
		image,
		onClick,
		overlayText,
		trackingId
	} = props;

	return (
		<ButtonBase
			trackingId={trackingId}
			type="button"
			className={styles.preview}
			onClick={onClick}
			aria-label={localize("_ViewImage")}
		>
			<img
				src={image.url}
				className={styles.image}
				alt={image.name}
			/>

			{overlayText && (
				<div className={styles.moreOverlay}>
					{overlayText}
				</div>
			)}
		</ButtonBase>
	);
};
ImagePreview.displayName = "ImagePreview";
